import type { ColumnDef } from "@tanstack/react-table";
import type { HandbookListItem } from "@/types/handbook";
import { HandbookStatusBadge } from "./handbook-status-badge";
import { HandbookActions, type HandbookAction } from "./handbook-actions";
import { formatDate } from "@/lib/format";
import { ImageIcon } from "lucide-react";

interface GetHandbookColumnsOptions {
  /** 부모 title lookup. row의 parentIds 각각이 가리키는 항목 제목을 즉시 찾기 위함. */
  parentTitleById?: Map<string, string>;
}

export function getHandbookColumns(
  onAction: (item: HandbookListItem, action: HandbookAction) => void,
  options: GetHandbookColumnsOptions = {},
): ColumnDef<HandbookListItem>[] {
  const { parentTitleById } = options;
  return [
    {
      accessorKey: "thumbnailUrl",
      header: "",
      cell: ({ row }) =>
        row.original.thumbnailUrl ? (
          <img
            src={row.original.thumbnailUrl}
            alt=""
            className="h-8 w-8 rounded object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-muted-foreground">
            <ImageIcon className="h-3.5 w-3.5" />
          </div>
        ),
    },
    {
      accessorKey: "title",
      header: "제목",
      cell: ({ row }) => (
        <div
          className="max-w-[300px] truncate text-sm font-medium"
          title={row.original.title}
        >
          {row.original.title}
        </div>
      ),
    },
    {
      accessorKey: "parentIds",
      header: "상위",
      cell: ({ row }) => {
        const parentIds = row.original.parentIds ?? [];
        if (parentIds.length === 0) {
          return <span className="text-xs text-muted-foreground/60">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {parentIds.map((pid) => {
              const parentTitle = parentTitleById?.get(pid);
              return (
                <span
                  key={pid}
                  className="rounded-md bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
                  title={pid}
                >
                  {parentTitle ?? `(id: ${pid.slice(-6)})`}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: "slug",
      header: "슬러그",
      cell: ({ row }) => (
        <code className="text-xs text-muted-foreground">
          {row.original.slug}
        </code>
      ),
    },
    {
      accessorKey: "tags",
      header: "태그",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "상태",
      cell: ({ row }) => <HandbookStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "publishedAt",
      header: "발행일",
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {formatDate(row.original.publishedAt, { format: "short" })}
        </div>
      ),
    },
    {
      accessorKey: "updatedAt",
      header: "수정일",
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {formatDate(row.original.updatedAt, { format: "short" })}
        </div>
      ),
    },
    {
      id: "actions",
      header: "작업",
      cell: ({ row }) => (
        <HandbookActions item={row.original} onAction={onAction} />
      ),
    },
  ];
}
